import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  return (
    <footer className="py-12 bg-neutral border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="navbar-content">
          {/* Left Column - Logo and Navigation */}
          <div className="navbar-left">
            {/* Logo */}
            <div className="logo">
              <div className="logo-icon">S</div>
              <div className="logo-text">Simulated Experience Studio</div>
            </div>

            {/* Desktop Navigation Links */}
            <div className="nav-links">
              <a href="#how-it-works" className="nav-link">How it works</a>
              <a href="#examples" className="nav-link">Use Cases</a>
              <a href="#datasets" className="nav-link">Datasets</a>
              <Link to="/synthetic" className="nav-link">Data Provider</Link>
              <a 
                href="#privacy" 
                onClick={(e) => {
                  e.preventDefault();
                  setPrivacyOpen(true);
                }}
                className="nav-link"
              >
                Privacy Policy
              </a>
            </div>
          </div>

          {/* Right Column - Copyright */}
          <div className="navbar-right">
            <p className="text-gray-400 text-sm">
              &copy; {new Date().getFullYear()} <a href="https://vossmoos.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-gray-300 transition-colors no-underline" style={{color: 'white !important', textDecoration: 'none !important', backgroundColor: 'transparent !important'}}>VossMoos</a>. All rights reserved.
            </p>
            <style jsx>{`
              a {
                color: white !important;
                text-decoration: none !important;
              }
              a:hover {
                color: #d1d5db !important;
              }
            `}</style>
          </div>
        </div>
      </div>

      {/* Terms of Services Modal */}
      {termsOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-neutral rounded-lg max-w-3xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-border">
              <h2 className="text-2xl font-bold text-white">Terms of Services</h2>
            </div>
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="space-y-4 text-gray-300">
                <p>
                  Welcome to VossMoos. By accessing and using our services, you agree to be bound by these Terms of Services.
                </p>
                <h3 className="text-lg font-semibold text-white">1. Use of Services</h3>
                <p>
                  Our services are designed to help IoT and robotics engineers create synthetic datasets for AI training. You agree to use our services only for lawful purposes and in accordance with these Terms.
                </p>
                <h3 className="text-lg font-semibold text-white">2. User Accounts</h3>
                <p>
                  When you create an account with us, you must provide accurate and complete information. You are responsible for safeguarding your account credentials and for all activities that occur under your account.
                </p>
                <h3 className="text-lg font-semibold text-white">3. Intellectual Property</h3>
                <p>
                  Our platform, including its content, features, and functionality, is owned by VossMoos and is protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
                </p>
                <h3 className="text-lg font-semibold text-white">4. User Data</h3>
                <p>
                  You retain all rights to your data. We will not share or sell your personal information to third parties without your consent, except as required by law. Please refer to our Privacy Policy for more information.
                </p>
                <h3 className="text-lg font-semibold text-white">5. Limitation of Liability</h3>
                <p>
                  To the maximum extent permitted by law, VossMoos shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues.
                </p>
                <h3 className="text-lg font-semibold text-white">6. Changes to Terms</h3>
                <p>
                  We may revise these Terms from time to time. The most current version will always be on our website. By continuing to access or use our services after revisions become effective, you agree to be bound by the revised terms.
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-border">
              <button 
                onClick={() => setTermsOpen(false)}
                className="btn btn-primary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {privacyOpen && (
        <div className="fixed inset-0 bg-black flex items-center justify-center z-[9999] p-4" style={{backgroundColor: 'rgba(0, 0, 0, 0.9)'}}>
          <div className="bg-black rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl border border-gray-600" style={{backgroundColor: '#000000'}}>
            <div className="p-6 border-b border-gray-600">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">Privacy Policy</h2>
                <button 
                  onClick={() => setPrivacyOpen(false)}
                  className="text-gray-400 hover:text-white text-2xl font-bold w-8 h-8 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6 max-h-[50vh] overflow-y-auto">
              <div className="space-y-4 text-gray-300">
                <p>
                  We take your privacy seriously. We collect information you provide directly to us (such as when you contact us for support) and automatically collect certain information when you visit our website, including your IP address, browser type, pages visited, and time spent on pages.
                </p>
                
                <p>
                  We use Google Analytics to analyze how visitors use our website. Google Analytics uses cookies to collect information about your use of our website, including pages visited, how you arrived at our site, your general geographic location, and device information. We have configured Google Analytics to anonymize IP addresses. You can opt out of Google Analytics by installing the Google Analytics Opt-out Browser Add-on.
                </p>

                <p>
                  We use your information to provide, maintain, and improve our services, process transactions, communicate with you, and understand how our website is used. We implement appropriate security measures to protect your personal information. We retain your information only as long as necessary to fulfill the purposes outlined in this policy. If you have any questions about this Privacy Policy, please contact us at aol@vossmoos.de.
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-600">
              <div className="flex justify-end">
                <button 
                  onClick={() => setPrivacyOpen(false)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;
